import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Instagram, Menu, X } from "lucide-react";
import { WhatsAppIcon } from "./icons";
import { BRAND } from "../data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/destinations", label: "Destinations" },
  { to: "/our-trips", label: "Our Happy Clients" },
  { to: "/contact", label: "Contact Us" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 border-b border-black/5 bg-white/95 backdrop-blur-xl transition-shadow duration-200 ${
        scrolled ? "shadow-md" : ""
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex shrink-0 items-center gap-2 xl:gap-3"
        >
          <img
            src="brand/mt-icon.png"
            alt="MT logo"
            data-testid="header-logo-icon"
            className="h-9 w-auto object-contain sm:h-10 xl:h-11"
          />
          <img
            src="brand/manvi-wordmark.png"
            alt="Manvi Travels — Come, see the world with us"
            data-testid="header-logo"
            className="h-9 w-auto object-contain sm:h-10 xl:h-11"
          />
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replaceAll(" ", "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-semibold transition-colors duration-200 xl:px-4 ${
                  isActive
                    ? "bg-navy text-white"
                    : "text-gray-700 hover:bg-black/5 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <a
            href={BRAND.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram-link"
            aria-label="Manvi Travels on Instagram"
            className="hidden h-10 w-10 items-center justify-center rounded-full text-navy transition-colors duration-200 hover:bg-black/5 xl:flex"
          >
            <Instagram className="h-5 w-5" />
          </a>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.03] xl:px-5"
          >
            <WhatsAppIcon className="h-4 w-4" />
            Plan a Holiday
          </a>
        </div>

        <button
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-navy lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-black/5 bg-white/95 px-4 pb-6 pt-2 backdrop-blur-xl lg:hidden">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `block rounded-xl px-4 py-3 text-base font-semibold ${
                  isActive ? "bg-navy text-white" : "text-gray-800"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="mt-4 flex items-center gap-3">
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-cta"
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-bold text-white"
            >
              <WhatsAppIcon className="h-4 w-4" />
              Plan a Holiday
            </a>
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-instagram-link"
              aria-label="Instagram"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-black/10 text-navy"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
